import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";
import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {DEFAULT_LINKVIEW_ICON} from "../../../domain/helpers/linkview-icons.helper";
import {LINK_TILE_ICONS} from "../../../domain/entities/link-tile-icon.const";
import {LINKVIEW_TITLE_MAX_LENGTH, type Linkview} from "../../../domain/entities/linkview.entity";

/**
 * What the dialog hands its caller as the payload of the OKAY command:
 * the trimmed title and the chosen icon — an `id` of the shared
 * `LINK_TILE_ICONS` list, or `null` when the preselected icon was clicked
 * again to deselect it (`ha-input-icon` treats an icon as optional).
 * `AbstractLinkviewRepository.create()` takes `string | null` and normalises
 * an absent/unknown key to the default, so `null` is safe to pass on.
 * (Until 2026-09-19 the payload was the bare title string.)
 */
export interface LinkviewCreateDialogResult {
    readonly title: string;
    readonly icon: string | null;
}

/**
 * What the dialog hands its caller as the payload of the OKAY command in
 * RENAME mode: only the trimmed title — the icon is not changeable there, so
 * none is offered and none is sent.
 */
export interface LinkviewRenameDialogResult {
    readonly title: string;
}

const HEADER_KEY_CREATE = `linkview.dialog.linkview-create.header`;
const HEADER_KEY_RENAME = `linkview.dialog.linkview-create.header-rename`;

/**
 * Minimal create dialog for a new Linkview (Anke, 2026-09-04): just a title
 * — unlike `ha-dialog-knowledge-object-edit` (its closest sibling), there is
 * no type picker (a Linkview has exactly one "type").
 *
 * TWO MODES, like that sibling (Anke, 2026-09-19: the sidebar row's 3-dot menu
 * offers "Umbenennen"): with `linkview === null` (the default) it creates;
 * with a `Linkview` set — `DialogHelper.showDialog(tag, {linkview})` assigns
 * the payload onto the element — it renames: prefilled title, its own header
 * and save label (`header-rename` / `button-rename`), and the icon field is
 * NOT rendered (the icon of an existing board stays as it is). The caller
 * gets `{title}` (`LinkviewRenameDialogResult`) instead of `{title, icon}`.
 *
 * Structured exactly like `ha-dialog-link-tile-edit` (this session's other
 * new non-entity dialog, itself modelled on `ha-dialog-knowledge-object
 * -edit`): plain `AbstractDialogComponent`, not `AbstractEntityDialogComponent`
 * — a Linkview has no backend repository/CommandBus to bind an advanced-form
 * to yet (see `linkview.repository.ts`'s own doc comment). Dispatches
 * `{title, icon}` (`LinkviewCreateDialogResult`) as the payload of an OKAY
 * command; `linkview-sidebar-list.component.ts` (the caller) is the one that
 * actually creates the Linkview and navigates to it.
 *
 * ICON FIELD (Anke, 2026-09-19; rebuilt 2026-09-20 on the house component):
 * the very same `<ha-input-icon>` field the TILE dialog uses
 * (`ha-dialog-link-tile-edit`), fed with the very same shared list
 * (`LINK_TILE_ICONS`) — no bespoke picker of its own any more. The component
 * is registered globally through `components/_inputs/index.ts`, so only the
 * list is imported here, exactly as the tile dialog does it.
 *
 * `link` is preselected. `ha-input-icon` allows DESELECTING (clicking the
 * selected icon again fires `value-changed` with `null`), so the payload's
 * `icon` is `string | null`; the repository maps a missing key back to the
 * default and `resolveLinkviewIcon()` keeps the sidebar row from ever
 * rendering without a glyph. Saving stays bound to a non-empty title only.
 *
 * TITLE LENGTH: the field is capped at `LINKVIEW_TITLE_MAX_LENGTH` (both
 * modes). The backend refuses a longer title and the refusal reaches the user
 * as a raw English "failed with status 500", so the cap has to sit on the
 * input. The number is never written here — it lives once, next to the
 * `Linkview` type, and must match `Linkview::TITLE_MAX_LENGTH` in the backend.
 *
 * Note the two glyph mechanics that coexist on purpose: `ha-input-icon` draws
 * the character from `codepoint` (data-driven list), while the SIDEBAR row
 * keeps `data-icon="<key>"` + a per-key `::before` rule in linkview_sidebar.css.
 */
@customElement(`ha-dialog-linkview-create`)
export class HaDialogLinkviewCreateComponent extends AbstractDialogComponent {

    /** The board being renamed, or `null` for create mode. */
    @property({type: Object})
    public linkview: Linkview | null = null;

    @state()
    private _title = ``;

    @state()
    private _icon: string | null = DEFAULT_LINKVIEW_ICON;

    public constructor() {
        super();
        this.closable = true;
        this.title = get(HEADER_KEY_CREATE);
    }

    private get _isRenameMode(): boolean {
        return this.linkview !== null;
    }

    /**
     * Re-seeds title/icon/header for whatever mode `linkview` says — ALWAYS on
     * (re)open, not only when that property changed.
     *
     * `DialogHelper.showDialog` hands back the ALREADY OPEN element of this kind
     * and merely `Object.assign`s the payload onto it, so ONE element is
     * re-pointed between the two modes while it stays on screen. Lit's dirty
     * check alone is not a dependable trigger for that: re-opening in CREATE
     * mode over a create dialog assigns `null` over `null`, `willUpdate` never
     * sees `linkview` in `changedProperties`, and a half-typed title would
     * survive into the "new board" the user just asked for. Seeding from
     * `open()` resets in BOTH directions, whatever the previous value was.
     *
     * `willUpdate` stays as the second entry point, for a plain `.linkview=`
     * assignment that does not go through a re-open.
     */
    public override open(): void {
        this._seedFromMode();
        super.open();
    }

    /** Re-seeds title/icon/header whenever the dialog is (re)pointed at a board
     *  or back at create mode. */
    protected willUpdate(changedProperties: PropertyValues): void {
        if (changedProperties.has(`linkview`)) {
            this._seedFromMode();
        }
    }

    private _seedFromMode(): void {
        this._title = this.linkview?.title ?? ``;
        this._icon = DEFAULT_LINKVIEW_ICON;
        this.title = get(this._isRenameMode ? HEADER_KEY_RENAME : HEADER_KEY_CREATE);
    }

    private get _isSubmitEnabled(): boolean {
        return this._title.trim().length > 0;
    }

    protected _renderMainContent(): HTMLTemplateResult {
        return html`
            <ha-form-linkview-create>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <ha-input-text data-type="title" class="max9999-flexrow"
                                   label=${translate(`linkview.dialog.linkview-create.label-title`)}
                                   required
                                   .maxlength=${LINKVIEW_TITLE_MAX_LENGTH}
                                   .value=${this._title}
                                   @value-changed=${this._onTitleChanged}
                    ></ha-input-text>
                    ${this._isRenameMode ? html`` : this._renderIconField()}
                </main>
            </ha-form-linkview-create>
        `;
    }

    /** The house icon field, structured exactly like the tile dialog's. */
    private _renderIconField(): HTMLTemplateResult {
        return html`
            <ha-input-icon data-type="icon" class="max9999-flexrow"
                           label=${translate(`linkview.dialog.linkview-create.label-icon`)}
                           .items=${LINK_TILE_ICONS}
                           .value=${this._icon}
                           @value-changed=${this._onIconChanged}
            ></ha-input-icon>
        `;
    }

    protected _renderFormFooter(): HTMLTemplateResult {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSubmitEnabled}
                        @click=${this._onClickSave}

                        data-label=${this._isRenameMode
                            ? translate(`linkview.dialog.linkview-create.button-rename`)
                            : translate(`general.save`)}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="cancel buttonform-marker secondary"
                        @click=${this._onClickCancel}

                        data-label=${translate(`linkview.dialog.linkview-create.button-cancel`)}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    private _onTitleChanged(event: CustomEvent<{ value: string }>): void {
        this._title = event.detail.value ?? ``;
    }

    private _onIconChanged(event: CustomEvent<{ value: string | null }>): void {
        this._icon = event.detail.value ?? null;
    }

    private _onClickSave(): void {
        if (!this._isSubmitEnabled) {
            return;
        }

        if (this._isRenameMode) {
            this.dispatchCommand<LinkviewRenameDialogResult>({
                command: DialogCommandName.OKAY,
                payload: {title: this._title.trim()},
            });
        } else {
            this.dispatchCommand<LinkviewCreateDialogResult>({
                command: DialogCommandName.OKAY,
                payload: {title: this._title.trim(), icon: this._icon},
            });
        }
        this._close();
    }

    private _onClickCancel(): void {
        this._close();
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    private _close(): void {
        DialogHelper.closeDialog(this.tagName);
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0,
        });
    }
}
